import { useState, useEffect } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";

interface QuantitySelectionProps {
  onContinue: (selectedQuantity: string) => void;
  onBack: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  productOptions?: any;
  productVariants?: any;
  frequencyGuidePage?: {
    title: string;
    body: string;
  } | null;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  chosenDetails: string;
}

export function QuantitySelection({
  onContinue,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  productOptions,
  productVariants,
  frequencyGuidePage,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  chosenDetails
}: QuantitySelectionProps) {
  const t = getNamespaceTranslations(language, "productSelection");
  const [selectedQuantity, setSelectedQuantity] = useState<string>(chosenDetails);
  const [selectionError, setSelectionError] = useState("");
  const quantityT = t?.quantitySelection;

  const handleContinue = () => {
    if (!selectedQuantity) {
      setSelectionError(quantityT?.selectionRequired || "This field is required");
      document.querySelector<HTMLElement>('[data-testid="quantity_radio-1"]')?.focus();
      return;
    }
    setSelectionError("");
    onContinue(selectedQuantity);
  };

  // Get quantity options from Shopify product options (which have translated values from @inContext)
  // Look for an option named "Quantity", "Size", "Count", etc.
  const quantityOption = productOptions?.find((opt: any) =>
    opt.name.toLowerCase().includes('quantity') ||
    opt.name.toLowerCase().includes('size') ||
    opt.name.toLowerCase().includes('count') ||
    opt.name.toLowerCase().includes('tablets') ||
    opt.name.toLowerCase().includes('cantidad')
  );

  // Get the translated quantity values directly from product options
  const quantityValues: string[] = quantityOption?.values || [];

  // Build variant data with metafields
  const variantData = quantityValues.map((quantityValue: string) => {
    // Find the variant that matches this quantity value
    const variant = productVariants?.edges?.find((edge: any) => {
      const quantityOpt = edge.node.selectedOptions?.find(
        (opt: any) => opt.name === quantityOption?.name
      );
      return quantityOpt?.value === quantityValue;
    })?.node;

    // Extract variant_quantity_badge metafield
    const badge = variant?.metafields?.find((mf: any) => mf?.key === 'variant_quantity_badge')?.value || null;

    return {
      value: quantityValue,
      badge,
    };
  });

  // Auto-select first variant on mount if no selection exists
  useEffect(() => {
    if (quantityValues.length > 0 && !selectedQuantity) {
      setSelectedQuantity(quantityValues[0]);
    }
  }, [quantityValues, selectedQuantity]);

  if (!quantityT) {
    return null;
  }

  if (quantityValues.length === 0) {
    console.warn('[QuantitySelection] No quantity options found in product');
    return null;
  }

  // Determine if we have a single variant (no choice needed)
  const isSingleVariant = quantityValues.length === 1;
  const displayTitle = isSingleVariant && quantityT.titleSingleVariant ? quantityT.titleSingleVariant : quantityT.title;
  const displaySubtitle = isSingleVariant ? (quantityT.subtitleSingleVariant ?? "") : quantityT.subtitle;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header logoUrl={logoUrl} onBack={onBack} onClose={onClose} />

      <div className="flex-1 flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
        {/* Progress Bar */}
        <div className="px-4 sm:px-6 pt-[30px]">
          <div className="max-w-[640px] mx-auto">
            {currentStep !== undefined &&
              totalSteps !== undefined &&
              sectionIndex !== undefined &&
              totalSections !== undefined &&
              sectionId !== undefined && (
              <ProgressBar
                currentStep={currentStep}
                totalSteps={totalSteps}
                sectionIndex={sectionIndex}
                totalSections={totalSections}
                sectionId={sectionId}
                language={language}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 pt-8 pb-[30px]">
          <div className="max-w-[640px] mx-auto">
            {/* Title */}
            <div className="mb-6">
              <h1
                id="quantity-selection-title"
                className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-black mb-2"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {displayTitle}
              </h1>
              {displaySubtitle && (
                <p
                  id="quantity-selection-description"
                  className="text-base leading-[22px] text-gray-500"
                  style={{ fontFamily: "TT Commons Pro" }}
                >
                  {displaySubtitle}
                </p>
              )}
            </div>

            {/* Quantity Options */}
            <div
              role="radiogroup"
              aria-labelledby="quantity-selection-title"
              aria-describedby={quantityT.subtitle ? "quantity-selection-description" : undefined}
              aria-required="true"
              className="flex flex-col gap-4"
            >
              {variantData.map((variant: any, index: number) => {
                const isSelected = selectedQuantity === variant.value;

                return (
                  <button
                    key={`${quantityOption?.name}-${index}`}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => {
                      setSelectedQuantity(variant.value);
                      if (selectionError) setSelectionError("");
                    }}
                    data-testid={`quantity_radio-${index + 1}`}
                    className={`w-full text-left transition-all bg-white rounded-[6px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D55FB] focus-visible:ring-offset-2 ${
                      isSelected
                        ? "border border-black"
                        : selectionError
                          ? "border border-[#E34D52]"
                          : "border border-[#E0E0E0]"
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <h3
                          className="text-black text-base leading-6 font-normal"
                          style={{ fontFamily: "TT Commons Pro" }}
                        >
                          {variant.value}
                        </h3>
                        {variant.badge && (
                          <span
                            className="ml-2 px-2.5 rounded-[20px] text-center text-sm leading-6 font-semibold"
                            style={{
                              fontFamily: "TT Commons Pro",
                              background: "#3CC34E1F"
                            }}
                          >
                            {variant.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="px-4 sm:px-6">
          <div className="max-w-[640px] mx-auto flex flex-col gap-4">
            {/* Frequency guide button - Only show if frequencyGuidePage exists */}
            {frequencyGuidePage && frequencyGuidePage.title && (
              <button
                type="button"
                data-frequency-modal-trigger
                aria-label={frequencyGuidePage.title}
                data-testid="quantity-frequency_guide"
                className="w-full flex items-center justify-between p-4 border border-[#E0E0E0] bg-[#F7F1F1] rounded-[6px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D55FB] focus-visible:ring-offset-2"
              >
                <span
                  className="text-black text-base leading-6 font-normal"
                  style={{ fontFamily: 'TT Commons Pro' }}
                >
                  {frequencyGuidePage.title}
                </span>
                <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M8.375 1.5C6.03125 1.5 3.90625 2.75 2.71875 4.75C1.5625 6.78125 1.5625 9.25 2.71875 11.25C3.90625 13.2812 6.03125 14.5 8.375 14.5C10.6875 14.5 12.8125 13.2812 14 11.25C15.1562 9.25 15.1562 6.78125 14 4.75C12.8125 2.75 10.6875 1.5 8.375 1.5ZM8.375 16C5.5 16 2.875 14.5 1.4375 12C0 9.53125 0 6.5 1.4375 4C2.875 1.53125 5.5 0 8.375 0C11.2188 0 13.8438 1.53125 15.2812 4C16.7188 6.5 16.7188 9.53125 15.2812 12C13.8438 14.5 11.2188 16 8.375 16ZM7.125 10.5H7.875V8.5H7.125C6.6875 8.5 6.375 8.1875 6.375 7.75C6.375 7.34375 6.6875 7 7.125 7H8.625C9.03125 7 9.375 7.34375 9.375 7.75V10.5H9.625C10.0312 10.5 10.375 10.8438 10.375 11.25C10.375 11.6875 10.0312 12 9.625 12H7.125C6.6875 12 6.375 11.6875 6.375 11.25C6.375 10.8438 6.6875 10.5 7.125 10.5ZM8.375 6C7.8125 6 7.375 5.5625 7.375 5C7.375 4.46875 7.8125 4 8.375 4C8.90625 4 9.375 4.46875 9.375 5C9.375 5.5625 8.90625 6 8.375 6Z" fill="black"/>
                </svg>
              </button>
            )}

            {/* Continue Button — always full colour; validate on submit */}
            {selectionError && (
              <p className="text-[#E34D52] text-sm" role="alert" style={{ fontFamily: "TT Commons Pro" }}>
                {selectionError}
              </p>
            )}
            <Button
              type="button"
              onClick={handleContinue}
              aria-label={quantityT.continue}
              data-testid="quantity-continue"
              className="w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] hover:bg-[#2347E8]"
            >
              {quantityT.continue}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
